import * as React from 'react';

import { useQuery } from '@apollo/client';

import GetAllSubdomainsAndProgramCurriculumDomainsQuery from 'bundles/enterprise-legacy-learner-home/queries/GetAllSubdomainsAndProgramCurriculumDomains.graphql';
import type { GetAllSubdomainsAndProgramCurriculumDomainsQuery as GetAllSubdomainsQueryType } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/GetAllSubdomainsAndProgramCurriculumDomains';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import DomainPillsContext from 'bundles/page-config-common/contexts/recommendations/DomainsPillsContext';
import type { PageConfig_DomainPillsComponentMetadataFragment } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfig_DomainPillsComponentMetadata';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

// The enterprise domains resource uses the the same underlying DomainsV1Resource that consumer uses, but performs some additional filtering
// of domains based on program curriculum. It also can return null if a program has chosen no domains and is not a "wildcard" program.
type EnterpriseDomain = NonNullable<
  GetAllSubdomainsQueryType['NaptimeQueries']['ProgramCurriculumDomainsV1Resource']['get']
>['domains'][0];
type EnterpriseSubdomain = NonNullable<GetAllSubdomainsQueryType['Subdomain']>['getAllSubdomains'][0];

// Convert enterprise domain and subdomain types to the consumer types so we can use the same context.
export const convertToConsumerDomains = (
  domains?: Array<EnterpriseDomain>,
  subdomains?: Array<EnterpriseSubdomain | null>
) => {
  return filterExistsOrDefault(domains).map((domain) => ({
    id: domain.id,
    name: domain.name,
    slug: domain.slug,
    subdomains: filterExistsOrDefault(subdomains)
      .filter((subdomain) => subdomain && domain.subdomainIds.includes(subdomain.id))
      .map((subdomain) => ({
        id: subdomain.id,
        name: subdomain.name,
      })),
  })) as PageConfig_DomainPillsComponentMetadataFragment['domains'];
};

/**
 * @description This provider is used to fetch the domains for the domain pills component on the program home page.
 */
const EnterpriseDomainPillsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const program = useProgram();
  const { data } = useQuery<GetAllSubdomainsQueryType>(GetAllSubdomainsAndProgramCurriculumDomainsQuery, {
    variables: {
      programId: program?.id,
    },
    skip: !program?.id,
    errorPolicy: 'all',
    ssr: false,
  });

  return (
    <DomainPillsContext.Provider
      value={{
        domains: convertToConsumerDomains(
          data?.NaptimeQueries?.ProgramCurriculumDomainsV1Resource?.get?.domains,
          data?.Subdomain?.getAllSubdomains
        ),
      }}
    >
      {children}
    </DomainPillsContext.Provider>
  );
};

export default EnterpriseDomainPillsProvider;
